import {Box} from '@mui/material';
import {DailyCareLogFormFooterButtons} from '../../../../components/daily-care-log/handle-task-form/daily-care-log-form-footer-buttons';
import {translations} from '../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {useEffect, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {ScreenPaths} from '../../../../enums';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {AddOccurrenceForm, AddOccurrenceFormData} from './add-occurrences-form';
import {EntityOccurrencesRepository} from '../../../../repositories/entity-occurrences';
import {useSnackBar} from '../../../../contexts/snack-bar/context';
import {DailyCareLogModal} from '../../../../components/daily-care-log/handle-task-form/daily-care-modal';
import {EntityOccurrenceTypeCategory} from '../../../../backend/responses/entity-occurrence';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import moment from 'moment';
import {RESPONSIBLE_NOT_DEFINED} from '../../office/professional-caregiver/professional-caregivers/shifts/actions-shift-flow/actions-shift-form';
import {useEntity} from '../../../../contexts/entity/context';
import {requestAttachmentStorageUrl} from '../unplanned-tasks/unplannedtask.daily-care-log.screen';
import {StorageType} from '../../../../backend/enums';
import {Attachment} from '../../../../backend/interfaces/attachment';

type AddOccurrencesDailyCareLogScreenProps = {
  seniors?: AffiliatedUser[];
  professionals?: AffiliatedUser[];
};

export const AddOccurrencesDailyCareLogScreen = ({
  seniors,
  professionals,
}: AddOccurrencesDailyCareLogScreenProps) => {
  const {t} = useTranslation();
  const {entityId}: {entityId: string} = useParams();
  let history = useHistory();
  const dailyCareLogContext = useDailyCareLog();
  const snackBarContext = useSnackBar();
  const location = useLocation();
  const {updateEntityOccurrencesCount} = useEntity();
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState<AddOccurrenceFormData | undefined>(undefined);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const date = searchParams.get('date');

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(
      true,
      t(translations.daily_care_log.occurrences.new_occurrence)
    );
    dailyCareLogContext.toggleBottomNavigation(false);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeFormChange = (
    values: AddOccurrenceFormData,
    errors: boolean,
    category?: EntityOccurrenceTypeCategory
  ) => {
    setFormData(values);

    if (
      category === 'patients' &&
      values.occurrencePatient &&
      values.occurrenceType &&
      values.occurrenceDetails &&
      values.occurrenceDate &&
      values.occurrenceTime &&
      !errors
    ) {
      setIsFormValid(true);
    } else if (
      category === 'general' &&
      values.occurrenceType &&
      values.occurrenceDetails &&
      values.occurrenceDate &&
      values.occurrenceTime &&
      !errors
    ) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  };

  const handleAddOccurrence = async () => {
    if (!formData) return;
    let attachments: Attachment[] | undefined = undefined;
    setSaving(true);

    if (formData.occurrenceFiles) {
      const result = await requestAttachmentStorageUrl(
        entityId,
        formData.occurrenceFiles,
        StorageType.OccurrenceAttachment
      );
      if (result === null) {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
        });
        setSaving(false);
        return;
      } else {
        attachments = result;
      }
    }

    try {
      await EntityOccurrencesRepository.instance.addOccurrence(entityId, {
        typeId: formData.occurrenceType,
        registeredAt: new Date(
          moment(formData.occurrenceDate).year(),
          moment(formData.occurrenceDate).month(),
          moment(formData.occurrenceDate).date(),
          moment(formData.occurrenceTime).hours(),
          moment(formData.occurrenceTime).minutes(),
          moment(formData.occurrenceTime).seconds()
        ),
        targetUserId: formData.occurrencePatient || undefined,
        responsibleUserId:
          formData.occurrenceResponsible &&
          formData.occurrenceResponsible !== RESPONSIBLE_NOT_DEFINED
            ? formData.occurrenceResponsible
            : undefined,
        siteName: formData.occurrenceLocal.trim() || null,
        details: formData.occurrenceDetails,
        attachments,
      });
      history.push(
        `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Occurrences}?date=${date}`
      );
      updateEntityOccurrencesCount();
      snackBarContext.setDailyCareLogToastNotification({
        severity: 'success',
        message: t(translations.daily_care_log.occurrences.add_success_toast),
      });
    } catch (e) {
      console.log(e);
      snackBarContext.setDailyCareLogToastNotification({
        severity: 'error',
        message: t(translations.daily_care_log.occurrences.add_error_toast),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="add-occurrence-form">
      <AddOccurrenceForm
        entityId={entityId}
        seniors={seniors}
        professionals={professionals}
        onFormChange={handleChangeFormChange}
      />
      <DailyCareLogFormFooterButtons
        canWrite
        editMode
        isCancelDisabled={false}
        isSaveDisabled={!isFormValid || saving}
        isSaveLoading={saving}
        saveLabel={t(translations.daily_care_log.occurrences.add)}
        onCancel={() => {
          if (
            formData &&
            (formData.occurrenceType !== '' ||
              formData.occurrenceLocal !== '' ||
              formData.occurrenceDetails !== '' ||
              formData.occurrenceResponsible !== '')
          ) {
            setIsCancelModalOpen(true);
          } else {
            history.push(
              `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Occurrences}?date=${date}`
            );
          }
        }}
        onSave={handleAddOccurrence}
        onEdit={() => {}}
      />
      <DailyCareLogModal
        open={isCancelModalOpen}
        title={t(translations.daily_care_log.occurrences.add_cancel_modal_title)}
        subtitle={t(translations.daily_care_log.occurrences.add_cancel_modal_subtitle)}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={async () => {
          history.push(
            `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Occurrences}?date=${date}`
          );
        }}
      />
    </Box>
  );
};
